/**
 * CFDI monetary helpers (2 decimal places, MXN).
 * Shared by frontend display and backend timbrado payload.
 * Keep SubTotal - Descuentos + ImpuestosTrasladados consistent with Total.
 */

function roundCfdiAmount(valor) {
  if (valor === null || valor === undefined || valor === "") return 0;
  const valorStr = String(valor).replace(/,/g, "");
  const n = parseFloat(valorStr);
  if (!Number.isFinite(n)) return 0;
  return parseFloat(n.toFixed(2));
}

function limpiarYConvertirAFloat(valor) {
  if (valor === null || valor === undefined) return 0;
  const valorStr = String(valor);
  const valorSinComas = valorStr.replace(/,/g, "");
  const num = parseFloat(valorSinComas);
  return Number.isFinite(num) ? num : 0;
}

function calcularIva16(base) {
  const baseRedondeada = roundCfdiAmount(base);
  return {
    Base: baseRedondeada,
    ImpuestoImporte: roundCfdiAmount(baseRedondeada * 0.16),
  };
}

function calcularTotalDesdeComponentes(
  subTotal,
  sumaDescuentos,
  sumaImpuestosTrasladados
) {
  return roundCfdiAmount(
    roundCfdiAmount(subTotal) -
      roundCfdiAmount(sumaDescuentos) +
      roundCfdiAmount(sumaImpuestosTrasladados)
  );
}

function facturaTieneDescuento(facturaRecibida) {
  const tieneConcepto =
    Array.isArray(facturaRecibida?.Conceptos) &&
    facturaRecibida.Conceptos.length > 0;
  const descuentoFactura = String(facturaRecibida?.Descuento ?? "").trim();
  const descuentoConcepto = tieneConcepto
    ? String(facturaRecibida.Conceptos[0].Descuento ?? "").trim()
    : "";
  return (
    (descuentoFactura !== "" && descuentoFactura !== "0") ||
    (descuentoConcepto !== "" && descuentoConcepto !== "0")
  );
}

/**
 * Same money fields the backend puts on facturaBody BEFORE normalize
 * (discount vs no-discount branches in facturacionBack.js).
 */
function construirMontosFacturaBody(facturaRecibida) {
  const conceptos = Array.isArray(facturaRecibida?.Conceptos)
    ? facturaRecibida.Conceptos
    : [];

  if (facturaTieneDescuento(facturaRecibida)) {
    return {
      Encabezado: {
        SubTotal: conceptos.reduce((acc, concepto) => {
          const totalDescuento = parseFloat(concepto.DescuentoCalculado) || 0;
          return acc + totalDescuento;
        }, parseFloat(facturaRecibida.SubTotal)),
        Total: facturaRecibida.Total,
      },
      Conceptos: conceptos.map((concepto) => {
        const cantidad = parseInt(concepto.Cantidad) || 1;
        const precioUnitario = limpiarYConvertirAFloat(concepto.PrecioUnitario);
        const baseSinDescuento = cantidad * precioUnitario;
        const descuentoPct = concepto.Descuento
          ? parseFloat(concepto.Descuento) / 100
          : 0;
        const descuentoImporte = roundCfdiAmount(baseSinDescuento * descuentoPct);
        const baseConDescuento = roundCfdiAmount(baseSinDescuento - descuentoImporte);
        const impuestoImporte = roundCfdiAmount(baseConDescuento * 0.16);

        return {
          Cantidad: cantidad,
          PrecioUnitario: precioUnitario,
          Importe: roundCfdiAmount(baseSinDescuento),
          Descuento: descuentoImporte || null,
          Impuestos: [
            {
              TipoImpuesto: 1,
              Impuesto: 2,
              Factor: 1,
              Base: baseConDescuento,
              Tasa: "0.160000",
              ImpuestoImporte: impuestoImporte,
            },
          ],
        };
      }),
    };
  }

  return {
    Encabezado: {
      SubTotal: limpiarYConvertirAFloat(facturaRecibida.SubTotal),
      Total: limpiarYConvertirAFloat(facturaRecibida.Total),
    },
    Conceptos: conceptos.map((concepto) => {
      const cantidad = parseInt(concepto.Cantidad) || 1;
      const precioUnitario = limpiarYConvertirAFloat(concepto.PrecioUnitario);
      const baseExacta = cantidad * precioUnitario;
      const base = roundCfdiAmount(baseExacta);
      const impuestoImporte = roundCfdiAmount(base * 0.16);

      return {
        Cantidad: cantidad,
        PrecioUnitario: precioUnitario,
        Importe: base,
        Impuestos: [
          {
            TipoImpuesto: 1,
            Impuesto: 2,
            Factor: 1,
            Base: base,
            Tasa: "0.160000",
            ImpuestoImporte: impuestoImporte,
          },
        ],
      };
    }),
  };
}

/**
 * Rounds each concept's ImpuestoImporte to 2 decimals and recomputes
 * Encabezado.Total from the same SubTotal, concept Descuentos, and
 * ImpuestoImporte values sent to the timbrado provider (avoids CFDI40119).
 */
function normalizarTotalesFacturaBody(facturaBody) {
  if (!facturaBody?.Conceptos || !facturaBody?.Encabezado) {
    return facturaBody;
  }

  let sumaImpuestos = 0;
  let sumaDescuentos = 0;

  const conceptos = facturaBody.Conceptos.map((concepto) => {
    const updated = { ...concepto };

    if (
      Object.prototype.hasOwnProperty.call(concepto, "Descuento") &&
      concepto.Descuento !== null &&
      concepto.Descuento !== undefined &&
      concepto.Descuento !== ""
    ) {
      const descuentoRedondeado = roundCfdiAmount(concepto.Descuento);
      updated.Descuento = descuentoRedondeado;
      if (descuentoRedondeado !== 0) {
        sumaDescuentos = roundCfdiAmount(sumaDescuentos + descuentoRedondeado);
      }
    }

    if (Array.isArray(concepto.Impuestos)) {
      updated.Impuestos = concepto.Impuestos.map((imp) => {
        const impuestoImporte = roundCfdiAmount(
          roundCfdiAmount(imp.Base) * 0.16
        );
        sumaImpuestos = roundCfdiAmount(sumaImpuestos + impuestoImporte);
        return {
          ...imp,
          ImpuestoImporte: impuestoImporte,
        };
      });
    }

    return updated;
  });

  const subTotal = roundCfdiAmount(facturaBody.Encabezado.SubTotal);
  const total = calcularTotalDesdeComponentes(
    subTotal,
    sumaDescuentos,
    sumaImpuestos
  );

  return {
    ...facturaBody,
    Encabezado: {
      ...facturaBody.Encabezado,
      SubTotal: subTotal,
      Total: total,
    },
    Conceptos: conceptos,
  };
}

/**
 * Full money pipeline used by both UI preview and timbrado.
 * Always rebuilds from concept Importes so a previously normalized
 * SubTotal is never treated as net and has discounts added twice.
 */
function calcularPayloadMontosCfdi(facturaRecibida) {
  const conceptos = Array.isArray(facturaRecibida?.Conceptos)
    ? facturaRecibida.Conceptos
    : [];
  const subTotalImportes = conceptos.reduce(
    (acc, concepto) => acc + limpiarYConvertirAFloat(concepto.Importe),
    0
  );
  return normalizarTotalesFacturaBody(
    construirMontosFacturaBody({
      ...facturaRecibida,
      Conceptos: conceptos,
      SubTotal: subTotalImportes,
    })
  );
}

function calcularMontosCfdi(facturaRecibida) {
  const normalized = calcularPayloadMontosCfdi(facturaRecibida);
  return {
    subTotal: roundCfdiAmount(normalized.Encabezado.SubTotal).toFixed(2),
    total: roundCfdiAmount(normalized.Encabezado.Total).toFixed(2),
    SubTotal: normalized.Encabezado.SubTotal,
    Total: normalized.Encabezado.Total,
  };
}

function aplicarMontosCfdiAFacturaBody(facturaBody, facturaRecibida) {
  const montos = calcularPayloadMontosCfdi(facturaRecibida);
  return {
    ...facturaBody,
    Encabezado: {
      ...facturaBody.Encabezado,
      SubTotal: montos.Encabezado.SubTotal,
      Total: montos.Encabezado.Total,
    },
    Conceptos: (facturaBody.Conceptos || []).map((concepto, i) => ({
      ...concepto,
      ...montos.Conceptos[i],
      CodigoUnidad: concepto.CodigoUnidad,
      CodigoProducto: concepto.CodigoProducto,
      Producto: concepto.Producto,
      ObjetoDeImpuesto: concepto.ObjetoDeImpuesto || "02",
    })),
  };
}

module.exports = {
  roundCfdiAmount,
  limpiarYConvertirAFloat,
  calcularIva16,
  calcularTotalDesdeComponentes,
  facturaTieneDescuento,
  construirMontosFacturaBody,
  normalizarTotalesFacturaBody,
  calcularPayloadMontosCfdi,
  calcularMontosCfdi,
  aplicarMontosCfdiAFacturaBody,
};
